import React, {useMemo} from 'react';

import {
  getConfidenceColor,
  NormalizedPolygon,
  PolygonCoord
} from './utils/polygonUtils';

const SVG_OVERLAY_STYLE: React.CSSProperties = {
  background: 'transparent',
  position: 'absolute',
  pointerEvents: 'none'
};

const POLYGON_STYLE: React.CSSProperties = {
  fill: 'none',
  strokeWidth: 1.5
};

interface LegacyPolygon {
  score?: number;
  value?: PolygonCoord[];
  page: number;
}

type OverlayPolygon = NormalizedPolygon | LegacyPolygon;

const getPolygonCoords = (item: OverlayPolygon): PolygonCoord[] => {
  if ('polygon' in item && item.polygon?.length) {
    return item.polygon;
  }
  if ('value' in item && item.value?.length) {
    return item.value;
  }

  return [];
};

const getConfidence = (item: OverlayPolygon): number => {
  if ('confidence' in item && item.confidence !== undefined) {
    return item.confidence;
  }
  if ('score' in item && item.score !== undefined) {
    return item.score;
  }

  return 0;
};

interface SVGBoundingBoxProps {
  polygons: OverlayPolygon[];
  imageDimension: {width: number; height: number};
  page: number;
  offsetX?: number;
  offsetY?: number;
}

const SVGBoundingBox: React.FC<SVGBoundingBoxProps> = ({
  polygons,
  imageDimension,
  page,
  offsetX = 0,
  offsetY = 0
}) => {
  const {width, height} = imageDimension;

  const renderedPolygons = useMemo(() => {
    if (!width || !height) {
      return [];
    }

    return polygons
      .filter((item) => item.page === page)
      .map((item) => {
        const coords = getPolygonCoords(item);
        const confidence = getConfidence(item);
        const points = coords
          .map((coord) => `${coord.X * width},${coord.Y * height}`)
          .join(' ');

        return {
          points,
          color: getConfidenceColor(confidence)
        };
      })
      .filter((item) => item.points.length > 0);
  }, [polygons, page, width, height]);

  if (!width || !height) {
    return null;
  }

  return (
    <svg
      width={width}
      height={height}
      style={{
        ...SVG_OVERLAY_STYLE,
        top: offsetY,
        left: offsetX
      }}
    >
      {renderedPolygons.map((polygon, index) => (
        <polygon
          key={index}
          points={polygon.points}
          style={{...POLYGON_STYLE, stroke: polygon.color}}
        />
      ))}
    </svg>
  );
};

export default React.memo(SVGBoundingBox);
